import { FunctionComponent, useState, useRef } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useTranslation } from '@/lib';
import { CreateTransactionModalFormModel } from '../CreateTransactionModal/CreateTransactionModalFormModel';
import { IconButton, InputAdornment, Popover } from '@mui/material';
import { AccessTime, CalendarToday } from '@mui/icons-material';
import { Dayjs } from 'dayjs';

interface TransactionDateFieldProps {
  control: Control<CreateTransactionModalFormModel>;
  errors: FieldErrors<CreateTransactionModalFormModel>;
  disabled?: boolean;
  isEditMode?: boolean;
}

export const TransactionDateField: FunctionComponent<TransactionDateFieldProps> = ({ control, errors, disabled, isEditMode = false }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const timeIconRef = useRef<HTMLButtonElement>(null);
  const calendarIconRef = useRef<HTMLButtonElement>(null);

  const formatTime = (date: Dayjs | null) => {
    if (!date) return '';
    return date.format('HH:mm');
  };

  const handleTimeIconClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCalendarIconClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setDatePickerOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Controller
      name="date"
      control={control}
      render={({ field }) => (
        <>
          <DatePicker
            label={t('date')}
            value={field.value}
            onChange={field.onChange}
            disabled={disabled}
            open={datePickerOpen}
            onOpen={() => setDatePickerOpen(true)}
            onClose={() => setDatePickerOpen(false)}
            slotProps={{
              textField: {
                fullWidth: true,
                error: errors.date !== undefined,
                helperText: errors.date?.message,
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {field.value && formatTime(field.value) !== '00:00' && (
                        <span className="text-xs text-gray-600 dark:text-gray-400 mr-1">{formatTime(field.value)}</span>
                      )}
                      {!disabled && (
                        <>
                          <IconButton
                            ref={timeIconRef}
                            onClick={handleTimeIconClick}
                            size="small"
                            title={isEditMode ? t('edit_time') : t('add_time')}
                          >
                            <AccessTime fontSize="small" />
                          </IconButton>
                          <IconButton ref={calendarIconRef} onClick={handleCalendarIconClick} size="small" title={t('select_date')}>
                            <CalendarToday fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </InputAdornment>
                  ),
                },
              },
            }}
          />

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <div className="p-4">
              <TimePicker
                label={t('time')}
                value={field.value}
                onChange={field.onChange}
                ampm={false}
                slotProps={{
                  textField: {
                    size: 'small',
                  },
                }}
              />
            </div>
          </Popover>
        </>
      )}
    />
  );
};
